import { CSSProperties, ReactElement } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { BackrsUserBadge } from '@backrs_ui/common-ui/src/atoms/BackrsUserBadge';

import { Box, SxProps, Theme } from '@mui/material';

import { NuggetProfilePanel } from './NuggetProfilePanel';

export type ProfileWidgetProps = Readonly<{
  abbreviatedName: string;
  location?: string;
  jobTitle?: string;
  employer?: string;
  school?: string;
  grade?: string;
  profileImageUrl?: string;
  profileCoverImageUrl?: string;
  contentSize?: 'SMALL' | 'LARGE';
  profileType: 'EXTERNAL' | 'INTERNAL';
  role?: 'professional' | 'student' | 'foundations';
  linkToProfile?: string;
  sharedTraitsText?: string;
  onClick?: () => void;
  subText?: string;
  fitContent?: boolean;
}>;

const ProfileWidget = ({
  abbreviatedName,
  location,
  jobTitle,
  employer,
  school,
  profileImageUrl,
  profileType,
  contentSize,
  role,
  linkToProfile,
}: ProfileWidgetProps): ReactElement => {
  return (
    <Box
      sx={{
        ...profileContainerStyle,
        gap: contentSize === 'SMALL' ? 2 : 4,
        backgroundColor:
          profileType === 'INTERNAL' ? appColors.color.taupe2 : `${appColors.color.blue4}85`,
      }}
    >
      {profileImageUrl ? (
        <Box
          sx={{
            ...profileImg,
            backgroundImage: `url(${profileImageUrl})`,
            width: contentSize === 'SMALL' ? '70px' : '90px',
            height: contentSize === 'SMALL' ? '70px' : '90px',
          }}
        >
          {role && <BackrsUserBadge variant={role} />}
        </Box>
      ) : (
        <Box>
          <BackrsIcon
            name="IconHeadshot"
            alt="Profile"
            height={contentSize === 'SMALL' ? 70 : 90}
            width={contentSize === 'SMALL' ? 70 : 90}
            style={genericIconStyle}
          />
          {role && <BackrsUserBadge variant={role} />}
        </Box>
      )}

      <Box>
        <Box sx={nameAndLinkContainerStyle}>
          <Box sx={abbreviatedNameStyle}>{abbreviatedName}</Box>
          {linkToProfile && (
            <Box
              sx={
                {
                  ...linkToProfileText,
                  backgroundColor:
                    profileType === 'INTERNAL' ? appColors.color.taupe1 : appColors.color.blue4,
                } as SxProps<Theme>
              }
              onClick={() => window.open(linkToProfile, '_blank')}
            >
              Profile
            </Box>
          )}
        </Box>
        <Box sx={profileInfoContainerStyle}>
          <Box sx={textGroupContainerStyle}>
            {location && (
              <Box sx={iconAndTextContainerStyle}>
                <Box>
                  <BackrsIcon name="IconMap" alt="Location" />
                </Box>
                <Box sx={profileInfoText}>{location}</Box>
              </Box>
            )}
            {jobTitle && (
              <Box sx={iconAndTextContainerStyle}>
                <Box>
                  <BackrsIcon name="IconBuilding" alt="Job Title" />
                </Box>
                <Box sx={profileInfoText}>{jobTitle}</Box>
              </Box>
            )}
          </Box>
          <Box sx={textGroupContainerStyle}>
            {employer && (
              <Box sx={iconAndTextContainerStyle}>
                <Box>
                  <BackrsIcon name="IconBusiness" alt="Employer" />
                </Box>
                <Box sx={profileInfoText}>{employer}</Box>
              </Box>
            )}
            {school && (
              <Box sx={iconAndTextContainerStyle}>
                <Box>
                  <BackrsIcon name="IconUniversity" alt="Job Title" />
                </Box>
                <Box sx={profileInfoText}>{school}</Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type NuggetProfileProps = Readonly<{
  variant:
    | {
        type: 'SMALL';
        abbreviatedName: string;
        location?: string;
        jobTitle?: string;
        employer?: string;
        school?: string;
        grade?: string;
        profileImageUrl?: string;
        profileCoverImageUrl?: string;
        contentSize?: 'SMALL' | 'LARGE';
        profileType: 'EXTERNAL' | 'INTERNAL';
        role?: 'professional' | 'student' | 'foundations';
        sharedTraitsText?: string;
        linkToProfile?: string;
        onClick?: () => void;
        subText?: string;
        fitContent?: boolean;
      }
    | {
        type: 'LARGE';
        abbreviatedName: string;
        location?: string;
        jobTitle?: string;
        employer?: string;
        school?: string;
        profileImageUrl?: string;
        profileType: 'EXTERNAL' | 'INTERNAL';
        role?: 'professional' | 'student' | 'foundations';
        linkToProfile?: string;
      };
}>;

export const NuggetProfile = ({ variant }: NuggetProfileProps): ReactElement => {
  if (variant.type === 'LARGE') {
    return (
      <Box maxWidth={'70%'}>
        <ProfileWidget {...variant} />
      </Box>
    );
  }

  return <NuggetProfilePanel {...variant} />;
};

const profileContainerStyle: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '70px 1fr',
  backgroundColor: appColors.color.taupe2,
  borderRadius: 4,
  padding: 2,
  mt: 2,
  overflow: 'hidden',
};

const nameAndLinkContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  gap: 4,
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginTop: '8px',
  paddingLeft: ({ backrs }) => backrs.spacing['spacing-lg'],
  paddingRight: ({ backrs }) => backrs.spacing['spacing-lg'],
};

const linkToProfileText: SxProps<Theme> = {
  borderRadius: 2,
  cursor: 'pointer',
  px: 2,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '9pt',
  fontWeight: 600,
  color: appColors.neutral.black,
  lineHeight: 1,
};

const profileImg: SxProps<Theme> = {
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
};

const abbreviatedNameStyle: SxProps<Theme> = {
  fontSize: '12pt',
  lineHeight: 1,
  fontWeight: 700,
  color: appColors.color.navy3,
};

const profileInfoText: SxProps<Theme> = {
  fontSize: '10.5pt',
  color: appColors.color.navy3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const iconAndTextContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};

const textGroupContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  overflow: 'hidden',
};

const profileInfoContainerStyle: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 2,
};

const genericIconStyle: CSSProperties = {
  overflow: 'hidden',
  borderRadius: '50%',
};
